import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { db, unwrap } from '@/lib/db';
import type { Announcement, NotificationRow } from '@/types/db';

export function useNotifications() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['me', 'notifications', user?.id],
    enabled: Boolean(user),
    // No realtime channel on Neon: poll while the app is open (and refetch on focus/reconnect).
    refetchInterval: 60 * 1000,
    queryFn: async () =>
      unwrap(await db.from('notifications').select('*').order('created_at', { ascending: false }).limit(100)) as NotificationRow[],
  });
}

export function useUnreadCount() {
  const { data } = useNotifications();
  return data?.filter((n) => !n.read_at).length ?? 0;
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (ids: string[] | 'all') => {
      let q = db.from('notifications').update({ read_at: new Date().toISOString() }).is('read_at', null);
      q = ids === 'all' ? q.eq('user_id', user!.id) : q.in('id', ids);
      unwrap(await q);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  });
}

export function useAnnouncement(id: string | undefined) {
  return useQuery({
    queryKey: ['announcements', id],
    enabled: Boolean(id),
    queryFn: async () => unwrap(await db.from('announcements').select('*').eq('id', id!).single()) as Announcement,
  });
}
